import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getMystudioErrorMessage,
  openSafeHttpsDownloadUrl,
  useMystudioPhotoDeliveriesV4,
  useMystudioPhotoDeliveryAssetsV4,
  useMystudioPhotoDownloadTicketV4,
} from "../../Common/api/v4";
import { MobileBottomTabBar } from "../MobileAppShell";
import { withMobileView } from "../MobileAppShell/mobileNavigation";
import {
  PHOTO_ARCHIVE_STATE,
  formatArchiveDate,
  getDaysRemaining,
  getRenditionUrl,
  normalizePhotoAlbums,
} from "./photoArchiveData";
import "./MyPhotoArchive.css";

const FILTERS = [
  { id: "all", label: "전체" },
  { id: "download", label: "원본 다운로드" },
  { id: "archive", label: "웹용 보관" },
];

function usePhotoArchiveAlbums() {
  const deliveryQuery = useMystudioPhotoDeliveriesV4();
  const deliveries = deliveryQuery.data?.data?.items || [];
  const rows = deliveries.map((delivery) => {
      const expiresAt = delivery.expiresAt || "";
      const expiresDate = expiresAt ? new Date(expiresAt) : null;
      const canDownload = delivery.photoCount > 0
        && (!expiresDate || Number.isNaN(expiresDate.getTime()) || expiresDate.getTime() >= Date.now());
      return {
        source: delivery.demo ? "demo" : "mystudio-v4",
        album_id: delivery.id,
        delivery_id: delivery.id,
        brand_id: "mystudio",
        schedule_id: delivery.scheduleId,
        title: delivery.title,
        office_name: delivery.officeName,
        schedule_date: delivery.scheduleDate,
        photo_type: delivery.type,
        photo_count: delivery.photoCount,
        preview_images: delivery.previewImages || [],
        download_expires_at: expiresAt,
        original_access: {
          can_download: canDownload,
          retention_expires_at: expiresAt,
          downloadable_count: canDownload ? delivery.photoCount : 0,
        },
        web_archive: {
          state: delivery.photoCount > 0 ? "READY" : "PENDING",
        },
      };
    });
  const albums = useMemo(
    () => normalizePhotoAlbums(rows, { allowLegacyExpiry: false }),
    [rows],
  );

  return {
    albums,
    configured: deliveryQuery.configured,
    isLoading: deliveryQuery.isLoading && albums.length === 0,
    error: deliveryQuery.error,
    unavailableReason: deliveryQuery.data?.data?.availability === "unavailable"
      ? deliveryQuery.data?.data?.reasonCode || "PHOTO_DELIVERY_UNAVAILABLE"
      : "",
    refetch: deliveryQuery.refetch,
  };
}

function getAlbumStatus(album) {
  const remainingDays = getDaysRemaining(album.expiryAt);

  if (album.state === PHOTO_ARCHIVE_STATE.PROCESSING) {
    return {
      badge: "사진 준비 중",
      title: "사진을 보기 좋게 준비하고 있어요",
      description: "웹용 사진 준비가 끝나면 바로 알려드릴게요.",
    };
  }

  if (album.state === PHOTO_ARCHIVE_STATE.PARTIAL) {
    return {
      badge: "일부 준비 중",
      title: "준비된 사진부터 볼 수 있어요",
      description: "나머지 사진도 순서대로 웹용 사진으로 준비하고 있어요.",
    };
  }

  if (album.state === PHOTO_ARCHIVE_STATE.ARCHIVED) {
    return {
      badge: "웹용 보관",
      title: "웹용 사진은 계속 볼 수 있어요",
      description: "원본 다운로드 기간이 종료되어 감상용 사진으로 보관 중이에요.",
    };
  }

  if (album.state === PHOTO_ARCHIVE_STATE.UNKNOWN) {
    return {
      badge: "사진 보관",
      title: "촬영 사진을 확인해 보세요",
      description: "원본 다운로드 가능 여부는 사진 상세에서 확인할 수 있어요.",
    };
  }

  const dateLabel = formatArchiveDate(album.expiryAt);
  return {
    badge: remainingDays === null
      ? "원본 다운로드 가능"
      : remainingDays === 0
        ? "다운로드 D-DAY"
        : `다운로드 D-${Math.max(0, remainingDays)}`,
    title: dateLabel ? `원본은 ${dateLabel}까지 보관돼요` : "원본을 다운로드할 수 있어요",
    description: album.state === PHOTO_ARCHIVE_STATE.EXPIRING
      ? "보관 기간이 곧 종료돼요. 필요한 원본을 지금 내려받아 주세요."
      : "원본이 필요한 경우 보관 기간 안에 내려받아 주세요.",
  };
}

function formatAlbumMeta(album) {
  const values = [
    formatArchiveDate(album.scheduleDate),
    album.studioName,
    album.photoCount ? `총 ${album.photoCount.toLocaleString("ko-KR")}장` : "사진 보기",
  ];
  return values.filter(Boolean).join(" · ");
}

function PhotoMosaic({ album }) {
  const images = album.previewImages.slice(0, 3);
  const remaining = album.photoCount ? Math.max(0, album.photoCount - images.length) : 0;

  return (
    <div className={`my-photo-mosaic has-${images.length}`} aria-label={`${album.title} 대표사진 ${images.length}장`}>
      {images.length === 0 ? (
        <span className="my-photo-mosaic__empty">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 6.5h16v11H4z" />
            <path d="m6.5 15 3.6-3.8 2.5 2.4 2.2-2.2 2.7 3.6" />
            <circle cx="16.8" cy="9.4" r="1.2" />
          </svg>
          <span>사진 준비 중</span>
        </span>
      ) : null}
      {images.map((image, index) => (
        <span className="my-photo-mosaic__item" key={`${getRenditionUrl(image)}-${index}`}>
          <img
            src={getRenditionUrl(image, index === 0 ? "viewer" : "grid")}
            alt={index === 0 ? `${album.title} 대표사진` : ""}
            loading="lazy"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.style.display = "none";
              event.currentTarget.parentElement?.classList.add("is-missing");
            }}
          />
          {index === 2 && remaining > 0 ? <b>+{remaining.toLocaleString("ko-KR")}</b> : null}
        </span>
      ))}
    </div>
  );
}

export function PhotoAlbumCard({ album, onView, onDownload, compact = false }) {
  const status = getAlbumStatus(album);
  const canView = ["READY", "PARTIAL"].includes(album.webState);

  return (
    <article className={`my-photo-album is-${album.state}${compact ? " is-compact" : ""}`}>
      <PhotoMosaic album={album} />
      <div className="my-photo-album__body">
        <div className="my-photo-album__heading">
          <span>
            <strong>{album.title}</strong>
            <small>{album.photoType}</small>
          </span>
          <b className="my-photo-album__badge">{status.badge}</b>
        </div>
        <p className="my-photo-album__meta">{formatAlbumMeta(album)}</p>
        <div className="my-photo-album__notice">
          <strong>{status.title}</strong>
          {!compact ? <span>{status.description}</span> : null}
        </div>
        <div className="my-photo-album__actions">
          <button type="button" className="my-photo-album__view" onClick={() => onView(album)} disabled={!canView}>
            {canView ? "사진 보기" : "사진 준비 중"}
          </button>
          {album.canDownload ? (
            <button type="button" className="my-photo-album__download" onClick={() => onDownload(album)}>
              {album.source === "demo" ? "샘플 원본 보기" : "원본 다운로드"}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function PhotoArchiveState({ type, onRetry }) {
  if (type === "loading") {
    return (
      <div className="my-photo-state" role="status">
        <span className="my-photo-state__spinner" aria-hidden="true" />
        <strong>촬영 사진을 불러오고 있어요</strong>
      </div>
    );
  }

  if (type === "error") {
    return (
      <div className="my-photo-state" role="alert">
        <strong>사진을 불러오지 못했어요</strong>
        <span>잠시 후 다시 시도해 주세요.</span>
        <button type="button" onClick={onRetry}>다시 시도</button>
      </div>
    );
  }

  if (type === "unavailable") {
    return (
      <div className="my-photo-state" role="status">
        <strong>안전한 사진 보기를 준비하고 있어요</strong>
        <span>사진 미리보기와 다운로드 연결이 완료되면 이 화면에서 확인할 수 있습니다.</span>
      </div>
    );
  }

  return (
    <div className="my-photo-state">
      <strong>아직 등록된 촬영 사진이 없어요</strong>
      <span>촬영이 완료되면 스튜디오에서 올린 사진이 여기에 모여요.</span>
    </div>
  );
}

export function MyPhotosSection({
  title = "나의 사진",
  description = "스튜디오에서 받은 사진을 촬영별로 모아보세요.",
  onNavigate,
}) {
  const routerNavigate = useNavigate();
  const { albums, configured, isLoading, error, unavailableReason, refetch } = usePhotoArchiveAlbums();
  const downloadMutation = useMystudioPhotoDownloadTicketV4();
  const recentAlbums = albums.slice(0, 2);
  const navigate = (target) => {
    if (onNavigate) {
      onNavigate(target);
      return;
    }
    routerNavigate(withMobileView(target));
  };

  const viewAlbum = (album) => navigate(`/mypage/photos?scheduleId=${encodeURIComponent(album.scheduleId)}`);
  const downloadAlbum = async (album) => {
    if (album.source === "demo") {
      const previewUrl = getRenditionUrl(album.previewImages?.[0], "viewer");
      const absoluteUrl = previewUrl ? new URL(previewUrl, window.location.origin).toString() : "";
      if (openSafeHttpsDownloadUrl(absoluteUrl)) return;
    }
    try {
      const response = await downloadMutation.mutateAsync(album.deliveryId || album.id);
      if (!openSafeHttpsDownloadUrl(response.data?.downloadUrl)) {
        throw new Error("안전한 다운로드 주소를 확인할 수 없습니다.");
      }
    } catch (downloadError) {
      window.alert(getMystudioErrorMessage(downloadError));
    }
  };

  return (
    <section className="my-photo-section" aria-labelledby="my-photo-section-title">
      <div className="my-photo-section__head">
        <span>
          <h2 id="my-photo-section-title">{title}</h2>
          <p>{description}</p>
        </span>
        <button type="button" onClick={() => navigate("/mypage/photos")}>
          전체보기
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
        </button>
      </div>
      <div className="my-photo-section__list">
        {!configured ? <PhotoArchiveState type="error" /> : null}
        {isLoading ? <PhotoArchiveState type="loading" /> : null}
        {configured && error && !isLoading ? <PhotoArchiveState type="error" onRetry={refetch} /> : null}
        {configured && !isLoading && !error && unavailableReason ? <PhotoArchiveState type="unavailable" /> : null}
        {configured && !isLoading && !error && !unavailableReason && recentAlbums.length === 0 ? <PhotoArchiveState type="empty" /> : null}
        {recentAlbums.map((album) => (
          <PhotoAlbumCard
            compact
            album={album}
            key={album.id}
            onView={viewAlbum}
            onDownload={downloadAlbum}
          />
        ))}
      </div>
    </section>
  );
}

function ArchiveHeader({ title, onBack }) {
  return (
    <header className="my-photo-page-header">
      <button type="button" onClick={onBack} aria-label="뒤로가기">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
      </button>
      <h1>{title}</h1>
      <span aria-hidden="true" />
    </header>
  );
}

function AlbumFilter({ value, onChange, counts }) {
  return (
    <div className="my-photo-filters" aria-label="사진 보관 상태 필터">
      {FILTERS.map((filter) => (
        <button
          type="button"
          key={filter.id}
          aria-pressed={value === filter.id}
          className={value === filter.id ? "is-active" : ""}
          onClick={() => onChange(filter.id)}
        >
          {filter.label}
          <span>{counts[filter.id] || 0}</span>
        </button>
      ))}
    </div>
  );
}

function PhotoLightbox({ photos, selectedIndex, onClose, onChange }) {
  const photo = selectedIndex === null ? null : photos[selectedIndex];
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const isOpen = Boolean(photo);

  useEffect(() => {
    if (!isOpen) return undefined;
    previousFocusRef.current = document.activeElement;
    const page = dialogRef.current?.closest(".my-photo-page");
    const backgroundNodes = page
      ? Array.from(page.querySelectorAll(".my-photo-page-header, .mobile-bottom-tab-bar, .my-photo-detail-hero, .my-photo-detail-grid-wrap"))
      : [];
    backgroundNodes.forEach((node) => {
      node.setAttribute("aria-hidden", "true");
      node.inert = true;
    });
    closeButtonRef.current?.focus();

    return () => {
      backgroundNodes.forEach((node) => {
        node.removeAttribute("aria-hidden");
        node.inert = false;
      });
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!photo) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onChange((selectedIndex - 1 + photos.length) % photos.length);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        onChange((selectedIndex + 1) % photos.length);
      }
      if (event.key === "Tab") {
        const focusable = Array.from(dialogRef.current?.querySelectorAll("button:not([disabled])") || []);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onChange, onClose, photo, photos.length, selectedIndex]);

  if (!photo) return null;

  return (
    <div ref={dialogRef} className="my-photo-lightbox" role="dialog" aria-modal="true" aria-label={`${photo.fileName} 크게 보기`} onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="my-photo-lightbox__toolbar">
        <span>{selectedIndex + 1} / {photos.length}</span>
        <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="사진 크게 보기 닫기">×</button>
      </div>
      <img src={photo.viewerUrl || photo.gridUrl} alt={photo.fileName} />
      {photos.length > 1 ? (
        <div className="my-photo-lightbox__nav">
          <button type="button" onClick={() => onChange((selectedIndex - 1 + photos.length) % photos.length)} aria-label="이전 사진">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
          </button>
          <button type="button" onClick={() => onChange((selectedIndex + 1) % photos.length)} aria-label="다음 사진">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AlbumDetail({ album, photos, isLoading, error, unavailable, onRetry, onDownload }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const status = getAlbumStatus(album);

  return (
    <>
      <section className="my-photo-detail-hero">
        <span className={`my-photo-detail-hero__badge is-${album.state}`}>{status.badge}</span>
        <h2>{album.title}</h2>
        <p>{formatAlbumMeta(album)}</p>
        <div className="my-photo-detail-hero__notice">
          <strong>{status.title}</strong>
          <span>{status.description}</span>
        </div>
        {album.canDownload ? (
          <button type="button" onClick={() => onDownload(album)}>
            {album.source === "demo" ? "샘플 원본 보기" : "원본 전체 다운로드"}
          </button>
        ) : null}
      </section>
      <section className="my-photo-detail-grid-wrap" aria-labelledby="my-photo-detail-grid-title">
        <div className="my-photo-detail-grid__head">
          <h2 id="my-photo-detail-grid-title">촬영 사진</h2>
          <span>{album.photoCount ? `총 ${album.photoCount.toLocaleString("ko-KR")}장` : `${photos.length}장`}</span>
        </div>
        {isLoading ? <PhotoArchiveState type="loading" /> : null}
        {error && !isLoading ? <PhotoArchiveState type="error" onRetry={onRetry} /> : null}
        {!isLoading && !error && unavailable ? <PhotoArchiveState type="unavailable" /> : null}
        {!isLoading && !error && !unavailable && photos.length === 0 ? <PhotoArchiveState type="empty" /> : null}
        {photos.length > 0 ? (
          <div className="my-photo-detail-grid">
            {photos.map((photo, index) => (
              <button type="button" key={photo.id} onClick={() => setSelectedIndex(index)} aria-label={`${photo.fileName} 크게 보기`}>
                <img
                  src={photo.gridUrl || photo.viewerUrl}
                  alt=""
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.style.display = "none";
                    event.currentTarget.parentElement?.classList.add("is-missing");
                  }}
                />
              </button>
            ))}
          </div>
        ) : null}
      </section>
      <PhotoLightbox photos={photos} selectedIndex={selectedIndex} onClose={() => setSelectedIndex(null)} onChange={setSelectedIndex} />
    </>
  );
}

export default function MyPhotoArchivePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const { albums, configured, isLoading, error, unavailableReason, refetch } = usePhotoArchiveAlbums();
  const selectedScheduleId = searchParams.get("scheduleId");
  const selectedAlbum = albums.find((album) => album.scheduleId === selectedScheduleId) || null;
  const selectedDeliveryId = selectedAlbum?.deliveryId || selectedAlbum?.id || "";
  const detailQuery = useMystudioPhotoDeliveryAssetsV4(selectedDeliveryId);
  const downloadMutation = useMystudioPhotoDownloadTicketV4();
  const photos = (detailQuery.data?.data?.items || []).map((photo, index) => ({
      id: photo.id,
      gridUrl: photo.thumbnailUrl || photo.url,
      viewerUrl: photo.url,
      fileName: `${selectedAlbum?.title || "촬영 사진"} ${index + 1}`,
      folderName: selectedAlbum?.title || "촬영 사진",
    }));
  const detailIsLoading = Boolean(selectedAlbum)
    && photos.length === 0
    && detailQuery.isLoading;
  const detailError = selectedAlbum && photos.length === 0
    ? detailQuery.error
    : null;
  const detailUnavailable = detailQuery.data?.data?.availability === "unavailable";
  const refetchDetail = detailQuery.refetch;
  const visibleAlbums = albums.filter((album) => {
    if (filter === "download") return album.canDownload;
    if (filter === "archive") return album.canDownload === false;
    return true;
  });
  const counts = {
    all: albums.length,
    download: albums.filter((album) => album.canDownload).length,
    archive: albums.filter((album) => album.canDownload === false).length,
  };
  const totalPhotoCount = albums.reduce((sum, album) => sum + (Number(album.photoCount) || 0), 0);

  const openAlbum = (album) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("scheduleId", album.scheduleId);
    setSearchParams(nextParams);
  };

  const handleBack = () => {
    if (selectedScheduleId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("scheduleId");
      setSearchParams(nextParams, { replace: true });
      return;
    }
    navigate(withMobileView("/tabs/mypage"));
  };

  const handleTabNavigate = (target) => navigate(withMobileView(target));
  const handleDownload = async (album) => {
    if (album.source === "demo") {
      const previewUrl = getRenditionUrl(album.previewImages?.[0], "viewer");
      const absoluteUrl = previewUrl ? new URL(previewUrl, window.location.origin).toString() : "";
      if (openSafeHttpsDownloadUrl(absoluteUrl)) return;
    }
    try {
      const response = await downloadMutation.mutateAsync(album.deliveryId || album.id);
      if (!openSafeHttpsDownloadUrl(response.data?.downloadUrl)) {
        throw new Error("안전한 다운로드 주소를 확인할 수 없습니다.");
      }
    } catch (downloadError) {
      window.alert(getMystudioErrorMessage(downloadError));
    }
  };

  return (
    <section className="my-photo-page mobile-app-frame">
      <ArchiveHeader title={selectedAlbum ? selectedAlbum.title : "나의 사진"} onBack={handleBack} />
      <main className="my-photo-page__scroll">
        {selectedScheduleId && !selectedAlbum && !isLoading ? (
          <PhotoArchiveState type="error" onRetry={() => setSearchParams({}, { replace: true })} />
        ) : null}
        {selectedAlbum ? (
          <AlbumDetail
            album={selectedAlbum}
            photos={photos}
            isLoading={detailIsLoading}
            error={selectedAlbum.source === "sample" ? null : detailError}
            unavailable={detailUnavailable}
            onRetry={refetchDetail}
            onDownload={handleDownload}
          />
        ) : !selectedScheduleId ? (
          <>
            <section className="my-photo-page__intro">
              <span>나의 촬영 기록</span>
              <h2>촬영별로 사진을 모아봤어요</h2>
              <p>
                {albums.length > 0 && totalPhotoCount > 0
                  ? `${albums.length}개 촬영 · 총 ${totalPhotoCount.toLocaleString("ko-KR")}장`
                  : "원본 다운로드 기간이 지나도 웹용 사진은 여기에서 계속 볼 수 있어요."}
              </p>
            </section>
            <AlbumFilter value={filter} onChange={setFilter} counts={counts} />
            <section className="my-photo-page__albums" aria-label="촬영별 사진 앨범">
              {!configured ? <PhotoArchiveState type="error" /> : null}
              {isLoading ? <PhotoArchiveState type="loading" /> : null}
              {configured && error && !isLoading ? <PhotoArchiveState type="error" onRetry={refetch} /> : null}
              {configured && !isLoading && !error && unavailableReason ? <PhotoArchiveState type="unavailable" /> : null}
              {configured && !isLoading && !error && !unavailableReason && visibleAlbums.length === 0 ? <PhotoArchiveState type="empty" /> : null}
              {visibleAlbums.map((album) => (
                <PhotoAlbumCard
                  album={album}
                  key={album.id}
                  onView={openAlbum}
                  onDownload={handleDownload}
                />
              ))}
            </section>
          </>
        ) : null}
      </main>
      <MobileBottomTabBar activePath="/tabs/mypage" onNavigate={handleTabNavigate} />
    </section>
  );
}
